import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { TaskService } from './task.service';
import { ErreursService } from './erreurs.service';
import { PollingService } from './polling.service';
import { identiteIntrouvable } from './identite-polling';
import { ChatTaskSyncService } from './chat-task-sync.service';
import { parseAttachment, conversationPreview } from './chat-attachment.util';
import { Conversation, Attachment, Message, Colleague } from '../models/chat.model';

// Ré-exportés : chat.service.ts reste le point d'import public de ces types
// (11 fichiers les importent d'ici) — déplacer leur définition vers
// core/models/chat.model.ts (Règle 12, fichier sous 500 lignes) ne doit rien
// changer côté consommateurs.
export type { Conversation, Attachment, Message, Colleague };

@Injectable({
    providedIn: 'root'
})
export class ChatService implements OnDestroy {
    private readonly erreurs = inject(ErreursService);
    private http = inject(HttpClient);
    private taskService = inject(TaskService);
    private taskSync = inject(ChatTaskSyncService);
    private polling = inject(PollingService);
    private unregisterPolling: (() => void) | null = null;
    private currentUserId: number | null = null;

    private readonly _conversations = signal<Conversation[]>([]);
    private readonly _messages = signal<Record<string, Message[]>>({});
    private readonly _colleagues = signal<Colleague[]>([]);
    private readonly _isLoading = signal(false);
    private readonly _error = signal<string | null>(null);

    readonly conversations = this._conversations.asReadonly();
    readonly messages = this._messages.asReadonly();
    readonly colleagues = this._colleagues.asReadonly();
    readonly isLoading = this._isLoading.asReadonly();
    readonly error = this._error.asReadonly();

    readonly totalUnread = computed(() =>
        this._conversations().reduce((sum, c) => sum + c.unread, 0)
    );

    private get chatApiUrl(): string {
        return (environment as any).apiUrls?.chat || '/api/chat';
    }

    ngOnDestroy() {
        this.stopPolling();
    }

    /** Initialize chat: fetch current user identity, colleagues + conversations from API */
    async initialize(): Promise<void> {
        this._isLoading.set(true);
        try {
            // Ensure local tasks are loaded for dedup
            await this.taskService.initialize();
            await this.fetchCurrentUserId();
            await this.fetchColleagues();
            await this.fetchConversations();
            this.startPolling();
        } catch (err: any) {
            console.warn('[ChatService] Initialize failed:', err?.message);
            this._error.set(this.erreurs.message(err));
        } finally {
            this._isLoading.set(false);
        }
    }

    /** Fetch the current user's DB id from backend */
    private async fetchCurrentUserId(): Promise<void> {
        try {
            const me = await firstValueFrom(
                this.http.get<{ id: number }>(`${this.chatApiUrl}/me`)
            );
            if (me?.id) {
                this.currentUserId = me.id;
            }
        } catch (err: any) {
            console.warn('[ChatService] Failed to fetch current user id:', err?.message);
        }
    }

    /** Fetch colleagues from backend */
    private async fetchColleagues(): Promise<void> {
        try {
            const colleagues = await firstValueFrom(
                this.http.get<any[]>(`${this.chatApiUrl}/colleagues`)
            );
            this._colleagues.set((colleagues || []).map(c => ({
                id: c.id,
                name: c.name || c.email || '',
                avatar: c.avatar || '',
                avatarUrl: c.avatarUrl || '',
                status: 'offline' as const,
                role: c.role || '',
                email: c.email || ''
            })));
        } catch (err: any) {
            console.warn('[ChatService] Failed to fetch colleagues:', err?.message);
        }
    }

    /** Vrai quand la derniere liste de conversations a ete refusee en 400 : le compte est introuvable cote serveur. */
    private identiteRefusee = false;

    /** Fetch conversations from backend */
    private async fetchConversations(): Promise<void> {
        this.identiteRefusee = false;
        try {
            const convs = await firstValueFrom(
                this.http.get<any[]>(`${this.chatApiUrl}/conversations`)
            );

            // Build a map of existing unread counts to preserve them across polling
            const existingUnreads = new Map<string, number>();
            for (const c of this._conversations()) {
                existingUnreads.set(c.id, c.unread);
            }

            const conversations: Conversation[] = (convs || []).map(c => {
                const lastMsg = c.lastMessage;
                const time = lastMsg?.createdAt
                    ? new Date(lastMsg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '';
                const timestamp = lastMsg?.createdAt ? new Date(lastMsg.createdAt).getTime() : new Date(c.updatedAt).getTime();

                const otherParticipant = c.participants?.find((p: any) => p.userId !== this.currentUserId);
                const convId = String(c.id);

                // Preserve existing unread count, or use backend value if available
                const existingUnread = existingUnreads.get(convId) ?? 0;
                const backendUnread = c.unreadCount ?? c.unread ?? undefined;

                return {
                    id: convId,
                    name: c.name || '',
                    lastMsg: lastMsg ? conversationPreview((lastMsg.type || 'text') as Message['type'], lastMsg.content) : '',
                    time,
                    timestamp,
                    unread: backendUnread !== undefined ? backendUnread : existingUnread,
                    avatar: otherParticipant?.avatar || c.name?.charAt(0)?.toUpperCase() || '',
                    avatarUrl: otherParticipant?.avatarUrl || '',
                    status: 'offline' as const,
                    participantIds: (c.participants || []).map((p: any) => String(p.userId)),
                    isGroup: c.isGroup || false
                };
            });

            // Sort by most recent
            conversations.sort((a, b) => b.timestamp - a.timestamp);
            this._conversations.set(conversations);
        } catch (err: any) {
            this.identiteRefusee = identiteIntrouvable(err);
            console.warn('[ChatService] Failed to fetch conversations:', err?.message);
        }
    }

    // ─── Message Operations ───

    getMessages(chatId: string): Message[] {
        return this._messages()[chatId] || [];
    }

    /** Fetch messages for a conversation from backend */
    async loadMessages(chatId: string): Promise<void> {
        try {
            const msgs = await firstValueFrom(
                this.http.get<any[]>(`${this.chatApiUrl}/conversations/${chatId}/messages?limit=50`)
            );

            const messages: Message[] = (msgs || []).map(m => {
                const isMe = m.senderUserId === this.currentUserId;
                const createdAt = new Date(m.createdAt);
                const type = (m.type || 'text') as Message['type'];
                const attachment = parseAttachment(type, m.content);
                return {
                    id: String(m.id),
                    conversationId: chatId,
                    sender: isMe ? 'me' as const : 'other' as const,
                    senderId: m.senderUserId,
                    senderName: m.senderName || '',
                    text: attachment ? '' : m.content,
                    time: createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    timestamp: createdAt.getTime(),
                    type,
                    status: 'read' as const,
                    attachment
                };
            });

            this._messages.update(prev => ({ ...prev, [chatId]: messages }));

            // Sync incoming task messages to local task list
            this.taskSync.syncIncomingTasks(messages);
        } catch (err: any) {
            console.warn('[ChatService] Failed to load messages:', err?.message);
        }
    }

    /** Notify sender that receiver has finished a task */
    async notifyTaskFinished(conversationId: string, taskText: string): Promise<void> {
        await this.sendMessage(conversationId, `TASK_FINISHED:${taskText}`, 'system');
    }

    /** Send a message via API. `attachment` porte une photo/document/vidéo déjà téléversée (files-service). */
    async sendMessage(
        chatId: string,
        text: string,
        type: Message['type'] = 'text',
        taskData?: Message['taskData'],
        attachment?: Attachment,
    ) {
        const now = new Date();
        const tempId = `temp-${Date.now()}`;
        const displayText = attachment ? '' : text;
        // Le backend ne connaît que `content` (texte) : la pièce jointe y voyage en JSON,
        // le frontend la reconnaît au retour via `type` (voir parseAttachment ci-dessus).
        const contentToSend = attachment ? JSON.stringify(attachment) : text;

        // Optimistic add
        const newMessage: Message = {
            id: tempId,
            conversationId: chatId,
            sender: 'me',
            text: displayText,
            time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            timestamp: now.getTime(),
            type,
            status: 'sent',
            taskData,
            attachment
        };

        this._messages.update(msgs => ({
            ...msgs,
            [chatId]: [...(msgs[chatId] || []), newMessage]
        }));

        // Update conversation preview
        this._conversations.update(convs => convs.map(c =>
            c.id === chatId
                ? {
                    ...c,
                    lastMsg: conversationPreview(type, contentToSend),
                    time: newMessage.time,
                    timestamp: now.getTime()
                }
                : c
        ));

        try {
            // Send to backend
            const result = await firstValueFrom(
                this.http.post<any>(`${this.chatApiUrl}/conversations/${chatId}/messages`, {
                    content: contentToSend,
                    type
                })
            );

            // Replace temp message with real one
            this._messages.update(msgs => ({
                ...msgs,
                [chatId]: (msgs[chatId] || []).map(m =>
                    m.id === tempId
                        ? { ...m, id: String(result.id), status: 'delivered' as const }
                        : m
                )
            }));
        } catch (err: any) {
            console.error('[ChatService] Failed to send message:', err?.message);
            // Mark as failed
            this._messages.update(msgs => ({
                ...msgs,
                [chatId]: (msgs[chatId] || []).map(m =>
                    m.id === tempId ? { ...m, status: 'sent' as const } : m
                )
            }));
        }
    }

    /** Mark conversation as read (locally + notify backend) */
    markAsRead(chatId: string) {
        this._conversations.update(convs =>
            convs.map(c => c.id === chatId ? { ...c, unread: 0 } : c)
        );
        // Notify backend (fire-and-forget)
        this.http.post(`${this.chatApiUrl}/conversations/${chatId}/read`, {}).subscribe({
            error: () => { /* silent */ }
        });
    }

    // ─── Conversation Management ───

    /** Start a new chat with a colleague - creates conversation via API */
    async startNewChat(colleague: Colleague): Promise<Conversation> {
        // Check if conversation already exists locally
        const existing = this._conversations().find(c =>
            !c.isGroup && c.participantIds.includes(String(colleague.id))
        );
        if (existing) return existing;

        try {
            // Create conversation via API
            const result = await firstValueFrom(
                this.http.post<{ id: number; created: boolean }>(`${this.chatApiUrl}/conversations`, {
                    otherUserId: colleague.id
                })
            );

            const conversation: Conversation = {
                id: String(result.id),
                name: colleague.name,
                lastMsg: '',
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                timestamp: Date.now(),
                unread: 0,
                avatar: colleague.avatar,
                avatarUrl: colleague.avatarUrl,
                status: colleague.status,
                participantIds: [String(colleague.id)],
                isGroup: false
            };

            // Add to local list if new
            if (!this._conversations().find(c => c.id === conversation.id)) {
                this._conversations.update(prev => [conversation, ...prev]);
            }

            // Initialize empty messages
            if (!this._messages()[conversation.id]) {
                this._messages.update(prev => ({ ...prev, [conversation.id]: [] }));
            }

            return conversation;
        } catch (err: any) {
            console.error('[ChatService] Failed to create conversation:', err?.message);
            // Fallback: create local-only conversation
            const fallback: Conversation = {
                id: `local-${Date.now()}`,
                name: colleague.name,
                lastMsg: '',
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                timestamp: Date.now(),
                unread: 0,
                avatar: colleague.avatar,
                avatarUrl: colleague.avatarUrl,
                status: colleague.status,
                participantIds: [String(colleague.id)],
                isGroup: false
            };
            this._conversations.update(prev => [fallback, ...prev]);
            return fallback;
        }
    }

    /** Delete a conversation */
    deleteConversation(chatId: string) {
        this._conversations.update(convs => convs.filter(c => c.id !== chatId));
        this._messages.update(msgs => {
            const copy = { ...msgs };
            delete copy[chatId];
            return copy;
        });
    }

    /** Search conversations */
    searchConversations(term: string): Conversation[] {
        if (!term.trim()) return this._conversations();
        const lower = term.toLowerCase();
        return this._conversations().filter(c =>
            c.name.toLowerCase().includes(lower) ||
            c.lastMsg.toLowerCase().includes(lower)
        );
    }

    // ─── Polling ───

    /** Abonnement idempotent au tick partagé de `PollingService` — pas de nouvelle boucle. */
    private startPolling() {
        if (this.unregisterPolling) return;
        this.unregisterPolling = this.polling.register(() => void this.pollForUpdates());
    }

    private stopPolling() {
        this.unregisterPolling?.();
        this.unregisterPolling = null;
    }

    /** Poll backend for new messages in active conversations */
    private async pollForUpdates(): Promise<void> {
        try {
            // Refresh conversations to get updated lastMessage / new conversations
            await this.fetchConversations();

            // Compte introuvable ou inactif cote serveur : retenter toutes les 5 s n'y change rien (voir identite-polling).
            if (this.identiteRefusee) {
                this.stopPolling();
                return;
            }

            // Refresh messages for conversations that have loaded messages
            const loadedChatIds = Object.keys(this._messages());
            for (const chatId of loadedChatIds) {
                await this.loadMessages(chatId);
            }
        } catch { /* silent */ }
    }

    // ─── Utils ───

    /** Set current user ID (called after auth) */
    setCurrentUserId(userId: number) {
        this.currentUserId = userId;
    }
}
